import type { PostLeadByExternalIdPhotosMutationRequest } from '../../../../../services/marketplace/generated/kubb/models/PostLeadByExternalIdPhotos';

export type PhotoCategory = 'exterior' | 'interior' | 'engineBay' | 'wheelsTires';

export type SlotDefinition = {
  id: string;
  category: PhotoCategory;
  label: string;
  photoType?: PostLeadByExternalIdPhotosMutationRequest['photoType'];
};

export type CategoryDefinition = {
  key: PhotoCategory;
  title: string;
  subtitle: string;
};

export const PRELIST_PHOTO_SLOT_ID = 'exterior-front-left';
export const PRELIST_ODOMETER_SLOT_ID = 'interior-odometer';

export const PHOTOS_TOTAL = 16;
export const MAX_PHOTO_SIZE_MB = 25;
export const MAX_PHOTO_SIZE_BYTES = MAX_PHOTO_SIZE_MB * 1024 * 1024;
export const PHOTO_INPUT_ACCEPT = 'image/*';

export const ADD_MORE_LATER_COPY = 'You can add more photos anytime after you list.';

const PHOTO_SLOT_DEFINITIONS = [
  { id: 'exterior-front', category: 'exterior', label: 'Front', photoType: 'front' },
  { id: PRELIST_PHOTO_SLOT_ID, category: 'exterior', label: 'Front-left corner' },
  {
    id: 'exterior-driver-side',
    category: 'exterior',
    label: 'Driver side',
    photoType: 'driver_side',
  },
  { id: 'exterior-rear-left', category: 'exterior', label: 'Rear-left corner' },
  { id: 'exterior-rear', category: 'exterior', label: 'Rear', photoType: 'rear' },
  { id: 'exterior-rear-right', category: 'exterior', label: 'Rear-right corner' },
  {
    id: 'exterior-passenger-side',
    category: 'exterior',
    label: 'Passenger side',
    photoType: 'passenger_side',
  },
  { id: 'exterior-front-right', category: 'exterior', label: 'Front-right corner' },
  { id: 'interior-dashboard', category: 'interior', label: 'Dashboard', photoType: 'dashboard' },
  { id: 'interior-rear-seats', category: 'interior', label: 'Rear seats', photoType: 'rear_seats' },
  { id: PRELIST_ODOMETER_SLOT_ID, category: 'interior', label: 'Odometer', photoType: 'odometer' },
  { id: 'interior-infotainment', category: 'interior', label: 'Infotainment screen' },
  { id: 'interior-trunk', category: 'interior', label: 'Trunk / cargo area', photoType: 'trunk' },
  { id: 'engine-bay', category: 'engineBay', label: 'Engine bay' },
  {
    id: 'wheels-front-driver',
    category: 'wheelsTires',
    label: 'Front driver-side wheel',
    photoType: 'front_tire',
  },
  { id: 'wheels-rear-driver', category: 'wheelsTires', label: 'Rear driver-side wheel' },
] as const;

export type PhotoSlotId = typeof PHOTO_SLOT_DEFINITIONS[number]['id'];

export const PHOTO_SLOTS: readonly SlotDefinition[] = PHOTO_SLOT_DEFINITIONS;

export const CATEGORIES: CategoryDefinition[] = [
  {
    key: 'exterior',
    title: 'Exterior',
    subtitle: 'Walk around your vehicle · recommended angles',
  },
  {
    key: 'interior',
    title: 'Interior',
    subtitle: 'Front seats, back seats, trunk, and details',
  },
  {
    key: 'engineBay',
    title: 'Engine bay',
    subtitle: 'Pop the hood',
  },
  {
    key: 'wheelsTires',
    title: 'Wheels & tires',
    subtitle: 'Driver-side front and rear',
  },
];

const findPhotoSlot = (slotId: string, fallback: SlotDefinition): SlotDefinition =>
  PHOTO_SLOTS.find((slot) => slot.id === slotId) ?? fallback;

export const PRELIST_PHOTO_SLOT = findPhotoSlot(PRELIST_PHOTO_SLOT_ID, {
  id: PRELIST_PHOTO_SLOT_ID,
  category: 'exterior',
  label: 'Front-left corner',
});

export const PRELIST_ODOMETER_SLOT = findPhotoSlot(PRELIST_ODOMETER_SLOT_ID, {
  id: PRELIST_ODOMETER_SLOT_ID,
  category: 'interior',
  label: 'Odometer',
});

export const DRIVERS_LICENSE_SLOT: SlotDefinition = {
  id: 'drivers-license',
  category: 'interior',
  label: "Driver's license",
};
